import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Send, Loader2, MessageSquare, ArrowLeft, ShieldCheck, Crown, User as UserIcon, Lock, X, Star, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { RoleBadge } from "@/components/RoleBadge";
import { RankBadge } from "@/components/RankBadge";
import { StreakBadge } from "@/components/StreakBadge";
import { toast } from "sonner";
import { playPopSound } from "@/lib/sound";

function relativeTime(iso) {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    const diff = Math.floor((Date.now() - t) / 1000);
    if (diff < 5) return "just now";
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(iso).toLocaleDateString();
}

function otherParticipant(conv, currentUserId) {
    const info = conv.participants_info || [];
    return info.find((p) => p && p.id !== currentUserId) || info[0] || null;
}

export default function Messages() {
    const { user, loading: authLoading } = useAuth();
    const nav = useNavigate();
    const { convId } = useParams();

    const [convs, setConvs] = useState([]);
    const [loadingConvs, setLoadingConvs] = useState(true);
    const [messages, setMessages] = useState([]);
    const [typing, setTyping] = useState([]);
    const [content, setContent] = useState("");
    const [sending, setSending] = useState(false);
    const [convDetail, setConvDetail] = useState(null); // full conv from messages endpoint (has closed, rating fields)
    const [closing, setClosing] = useState(false);
    const [ratingValue, setRatingValue] = useState(0);
    const [ratingHover, setRatingHover] = useState(0);
    const [ratingDescription, setRatingDescription] = useState("");
    const [submittingRating, setSubmittingRating] = useState(false);
    const lastIsoRef = useRef(null);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (!authLoading && !user) nav("/login?next=/messages");
    }, [authLoading, user, nav]);

    const isStaff = user && (user.role === "admin" || user.role === "support");

    const fetchConvs = useCallback(async () => {
        try {
            const res = await api.get("/dm/conversations");
            setConvs(res.data.items || []);
        } catch (e) {} finally { setLoadingConvs(false); }
    }, []);

    useEffect(() => {
        if (!user) return;
        fetchConvs();
        const id = setInterval(fetchConvs, 6000);
        return () => clearInterval(id);
    }, [fetchConvs, user]);

    const fetchMessages = useCallback(async (full = false) => {
        if (!convId) return;
        try {
            const params = {};
            if (!full && lastIsoRef.current) params.since = lastIsoRef.current;
            const res = await api.get(`/dm/conversations/${convId}/messages`, { params });
            const items = res.data.items || [];
            setTyping((res.data.typing || []).filter((t) => t.user_id !== user?.id));
            // Track full conv state (closed/rating) for UI rendering
            if (res.data.conversation) {
                setConvDetail(res.data.conversation);
            }
            if (items.length || full) {
                if (full) {
                    setMessages(items);
                } else {
                    setMessages((cur) => {
                        const ids = new Set(cur.map((m) => m.id));
                        return [...cur, ...items.filter((i) => !ids.has(i.id))];
                    });
                }
                if (items.length) lastIsoRef.current = items[items.length - 1].created_at;
                requestAnimationFrame(() => {
                    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
                });
            }
        } catch (e) {
            if (e?.response?.status === 403) {
                toast.error("You can't access this conversation");
                nav("/messages");
            }
        }
    }, [convId, user, nav]);

    useEffect(() => {
        lastIsoRef.current = null;
        setMessages([]);
        setConvDetail(null);
        setRatingValue(0);
        setRatingDescription("");
        if (convId) fetchMessages(true);
    }, [convId, fetchMessages]);

    useEffect(() => {
        if (!convId) return;
        const id = setInterval(() => fetchMessages(false), 3500);
        return () => clearInterval(id);
    }, [convId, fetchMessages]);

    const send = async (e) => {
        e?.preventDefault();
        const c = content.trim();
        if (!c || !convId) return;
        setSending(true);
        try {
            await api.post(`/dm/conversations/${convId}/messages`, { content: c });
            setContent("");
            playPopSound();
            await fetchMessages(false);
            await fetchConvs();
        } catch (e) {
            if (e?.response?.status === 423) {
                toast.error("This conversation has ended.");
                await fetchMessages(true);
            } else {
                toast.error("Failed to send");
            }
        } finally { setSending(false); }
    };

    const closeConv = async () => {
        if (!convId || !window.confirm("End this conversation? The user will be asked to rate the support they received.")) return;
        setClosing(true);
        try {
            await api.post(`/dm/conversations/${convId}/close`);
            toast.success("Conversation ended");
            await fetchMessages(true);
            await fetchConvs();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to end conversation");
        } finally { setClosing(false); }
    };

    const submitRating = async () => {
        if (!ratingValue || ratingValue < 1) {
            toast.error("Please pick a star rating first");
            return;
        }
        setSubmittingRating(true);
        try {
            await api.post(`/dm/conversations/${convId}/rate`, {
                rating: ratingValue,
                description: ratingDescription.trim() || null,
            });
            toast.success("Thanks for your feedback!");
            await fetchMessages(true);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to submit rating");
        } finally { setSubmittingRating(false); }
    };

    const onTyping = async () => {
        if (!convId) return;
        try { await api.post(`/dm/conversations/${convId}/typing`); } catch (e) {}
    };

    if (authLoading) {
        return (
            <div className="min-h-screen pp-bg-0 text-white">
                <Nav />
                <section className="py-12 max-w-5xl mx-auto px-4">
                    <div className="h-8 w-40 mb-4 rounded pp-bg-1 animate-pulse" />
                    <div className="grid md:grid-cols-3 gap-4">
                        <div className="h-96 rounded-xl pp-bg-1 animate-pulse" />
                        <div className="h-96 rounded-xl pp-bg-1 animate-pulse md:col-span-2" />
                    </div>
                </section>
                <Footer />
            </div>
        );
    }
    if (!user) return null;

    const activeConv = convs.find((c) => c.id === convId);
    const other = activeConv ? otherParticipant(activeConv, user.id) : null;

    return (
        <div className="min-h-screen pp-bg-0 text-white">
            <Nav />
            <section className="py-8 sm:py-12">
                <div className="max-w-5xl mx-auto px-4">
                    <div className="mb-5">
                        <p className="text-xs font-mono uppercase text-[#22d3ee] tracking-widest">Direct Messages</p>
                        <h1 className="font-display text-3xl sm:text-4xl tracking-widest inline-flex items-center gap-2"><MessageSquare className="w-6 h-6" /> MESSAGES</h1>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Conversation list */}
                        <Card className={`pp-bg-1 border-[#242436] text-white md:col-span-1 ${convId ? "hidden md:block" : ""}`}>
                            <CardContent className="p-3">
                                <p className="text-xs font-mono uppercase text-[#8b8ba0] mb-2">{isStaff ? "All conversations" : "Your DMs"}</p>
                                {loadingConvs ? (
                                    <div className="py-8 text-center text-sm text-[#8b8ba0]"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>
                                ) : convs.length === 0 ? (
                                    <div className="py-8 text-center text-sm text-[#8b8ba0]">
                                        {isStaff ? "No conversations yet." : "No DMs yet. Tap “Need Help?” anywhere on the site to request support."}
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-1" data-testid="messages-list">
                                        {convs.map((c) => {
                                            const o = otherParticipant(c, user.id);
                                            const isActive = c.id === convId;
                                            const accent = o?.role === "admin" ? "#facc15" : o?.role === "support" ? "#22d3ee" : "#a855f7";
                                            return (
                                                <Link
                                                    key={c.id}
                                                    to={`/messages/${c.id}`}
                                                    className={`flex items-center gap-3 rounded-md px-3 py-2 ${isActive ? "bg-[#1b1b26]" : "hover:bg-[#14141c]"}`}
                                                    data-testid="messages-conversation-item"
                                                >
                                                    <span className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm text-white flex-shrink-0" style={{ background: accent }}>
                                                        {(o?.display_name || "?").charAt(0).toUpperCase()}
                                                    </span>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-1">
                                                            <p className="font-medium text-white truncate">{o?.display_name || "User"}</p>
                                                            <RoleBadge role={o?.role} />
                                                        </div>
                                                        <p className="text-xs text-[#8b8ba0] truncate">{c.last_message_preview || "No messages yet"}</p>
                                                    </div>
                                                    <span className="text-xs text-[#8b8ba0] font-mono ml-1">{relativeTime(c.last_message_at)}</span>
                                                </Link>
                                            );
                                        })}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Conversation pane */}
                        <Card className={`pp-bg-1 border-[#242436] text-white md:col-span-2 ${!convId ? "hidden md:block" : ""}`}>
                            <CardContent className="p-0 flex flex-col h-[60vh] sm:h-[68vh]">
                                {!convId ? (
                                    <div className="flex-1 flex items-center justify-center text-center text-[#8b8ba0] text-sm p-6">
                                        Select a conversation to start chatting.
                                    </div>
                                ) : (
                                    <>
                                        <div className="px-4 py-3 border-b border-[#242436] flex items-center gap-2">
                                            <Button asChild variant="ghost" size="icon" className="md:hidden text-white"><Link to="/messages"><ArrowLeft className="w-5 h-5" /></Link></Button>
                                            <span className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs text-white" style={{ background: other?.role === "admin" ? "#facc15" : other?.role === "support" ? "#22d3ee" : "#a855f7" }}>
                                                {(other?.display_name || "?").charAt(0).toUpperCase()}
                                            </span>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-semibold text-white inline-flex items-center gap-1">{other?.display_name || "User"} <RoleBadge role={other?.role} /></p>
                                                <p className="text-xs text-[#8b8ba0] truncate">{activeConv?.subject || ""}</p>
                                            </div>
                                            {/* Staff: End Conversation button (only when not yet closed) */}
                                            {isStaff && convDetail && !convDetail.closed && (
                                                <Button onClick={closeConv} disabled={closing} variant="secondary" className="bg-[#1a0606] border border-[#ef4444]55 text-[#ef4444] hover:bg-[#2a0c0c] hover:text-white" data-testid="messages-end-conversation-button">
                                                    {closing ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="inline-flex items-center gap-1"><X className="w-4 h-4" /> End Convo</span>}
                                                </Button>
                                            )}
                                            {convDetail?.closed && (
                                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono uppercase text-[#facc15] border border-[#facc15]55 bg-[#1f1a05]" data-testid="messages-closed-badge">
                                                    <Lock className="w-3 h-3" /> Ended
                                                </span>
                                            )}
                                        </div>

                                        {/* Inline rating card for the non-staff user when convo is closed and not yet rated */}
                                        {!isStaff && convDetail?.closed && !convDetail?.rating && (
                                            <div className="px-4 py-4 border-b border-[#242436] bg-gradient-to-br from-[#1a0f2e] to-[#0a0a0f]" data-testid="messages-rating-card">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Star className="w-4 h-4 text-[#facc15]" />
                                                    <p className="font-display text-lg tracking-widest text-white">RATE YOUR SUPPORT EXPERIENCE</p>
                                                </div>
                                                <p className="text-xs text-[#b7b7c6] mb-3">How was your conversation with {other?.display_name || "support"}? Your feedback helps us improve. Only the host can see your rating.</p>
                                                <div className="flex items-center gap-1 mb-3" role="radiogroup" aria-label="Rating">
                                                    {[1, 2, 3, 4, 5].map((n) => (
                                                        <button
                                                            key={n}
                                                            type="button"
                                                            aria-label={`${n} star${n > 1 ? "s" : ""}`}
                                                            onClick={() => setRatingValue(n)}
                                                            onMouseEnter={() => setRatingHover(n)}
                                                            onMouseLeave={() => setRatingHover(0)}
                                                            className="p-1 transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#facc15] rounded"
                                                            data-testid={`messages-rating-star-${n}`}
                                                        >
                                                            <Star
                                                                className="w-7 h-7"
                                                                style={{
                                                                    fill: (ratingHover ? n <= ratingHover : n <= ratingValue) ? "#facc15" : "transparent",
                                                                    color: (ratingHover ? n <= ratingHover : n <= ratingValue) ? "#facc15" : "#4a4a5e",
                                                                }}
                                                            />
                                                        </button>
                                                    ))}
                                                    <span className="ml-2 text-sm font-mono text-[#b7b7c6]">{ratingValue ? `${ratingValue}/5` : "Pick a rating"}</span>
                                                </div>
                                                <Textarea
                                                    value={ratingDescription}
                                                    onChange={(e) => setRatingDescription(e.target.value.slice(0, 500))}
                                                    placeholder="Optional: tell the host more about your experience (max 500 chars)"
                                                    rows={2}
                                                    className="pp-bg-0 border-[#242436] text-white text-sm"
                                                    data-testid="messages-rating-description"
                                                />
                                                <div className="flex items-center justify-between mt-2">
                                                    <span className="text-[10px] font-mono text-[#8b8ba0]">{ratingDescription.length}/500</span>
                                                    <Button onClick={submitRating} disabled={!ratingValue || submittingRating} className="bg-[#facc15] hover:brightness-110 text-[#07130b] font-semibold" data-testid="messages-rating-submit">
                                                        {submittingRating ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="inline-flex items-center gap-1"><Check className="w-4 h-4" /> Submit Rating</span>}
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                        {/* Already rated indicator */}
                                        {!isStaff && convDetail?.closed && convDetail?.rating && (
                                            <div className="px-4 py-3 border-b border-[#242436] bg-[#1f1a05] flex items-center gap-2" data-testid="messages-rated-indicator">
                                                <Check className="w-4 h-4 text-[#facc15]" />
                                                <p className="text-sm text-white">Thanks for rating this conversation</p>
                                                <span className="inline-flex items-center gap-0.5 ml-1">
                                                    {[1, 2, 3, 4, 5].map((n) => (
                                                        <Star key={n} className="w-3.5 h-3.5" style={{ fill: n <= convDetail.rating ? "#facc15" : "transparent", color: n <= convDetail.rating ? "#facc15" : "#4a4a5e" }} />
                                                    ))}
                                                </span>
                                            </div>
                                        )}

                                        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-thread">
                                            <AnimatePresence>
                                                {messages.length === 0 ? (
                                                    <p className="text-center text-[#8b8ba0] text-sm py-12">No messages yet. Say hi!</p>
                                                ) : messages.map((m) => {
                                                    const mine = m.from_user_id === user.id;
                                                    const isSystem = m.system === true;
                                                    const accent = m.from_role === "admin" ? "#facc15" : m.from_role === "support" ? "#22d3ee" : "#a855f7";
                                                    if (isSystem) {
                                                        return (
                                                            <motion.div key={m.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className="flex justify-center" data-testid="messages-system-message">
                                                                <div className="text-xs font-mono uppercase tracking-widest text-[#facc15] bg-[#1f1a05] border border-[#facc15]44 rounded-full px-3 py-1 inline-flex items-center gap-1">
                                                                    <Lock className="w-3 h-3" /> {m.content.replace(/^—\s*/, "")}
                                                                </div>
                                                            </motion.div>
                                                        );
                                                    }
                                                    return (
                                                        <motion.div key={m.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid="messages-message">
                                                            <div className={`max-w-[75%] rounded-2xl px-3 py-2 ${mine ? "bg-[#6f3fec] text-white" : "pp-bg-2 text-white border border-[#242436]"}`} style={!mine ? { boxShadow: `0 0 0 1px ${accent}22` } : null}>
                                                                {!mine && <p className="text-[10px] font-mono uppercase mb-0.5 inline-flex items-center gap-1.5" style={{ color: accent }}>
                                                                    <Link to={`/u/${m.from_user_id}`} className="hover:underline">{m.from_display_name}</Link> · {m.from_role}
                                                                    {m.from_rank && <RankBadge rank={{ key: m.from_rank, streak: m.from_streak }} size="sm" showLabel={false} />}
                                                                    {m.from_streak ? <StreakBadge streak={m.from_streak} size="sm" /> : null}
                                                                </p>}
                                                                <p className="whitespace-pre-wrap break-words text-sm">{m.content}</p>
                                                                <p className="text-[10px] mt-0.5 opacity-70 text-right">{relativeTime(m.created_at)}</p>
                                                            </div>
                                                        </motion.div>
                                                    );
                                                })}
                                            </AnimatePresence>
                                        </div>
                                        {typing.length > 0 && !convDetail?.closed && (
                                            <div className="px-4 py-1 text-xs font-mono text-[#22d3ee]" data-testid="messages-typing">
                                                {typing.map((t) => t.display_name).join(", ")} is typing…
                                            </div>
                                        )}
                                        <div className="border-t border-[#242436] p-3">
                                            {convDetail?.closed ? (
                                                <div className="flex items-center gap-2 text-sm text-[#8b8ba0] justify-center py-2" data-testid="messages-locked-input">
                                                    <Lock className="w-4 h-4 text-[#facc15]" />
                                                    <span className="font-mono text-xs uppercase">This conversation has ended. No new messages can be sent.</span>
                                                </div>
                                            ) : (
                                                <form onSubmit={send} className="flex items-center gap-2">
                                                    <Input value={content} onChange={(e) => { setContent(e.target.value); onTyping(); }} placeholder="Type a message…" className="pp-bg-0 border-[#242436] text-white" data-testid="messages-input" />
                                                    <Button type="submit" disabled={sending || !content.trim()} className="bg-[#22d3ee] hover:brightness-110 text-[#062229] font-semibold" data-testid="messages-send-button">
                                                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                                    </Button>
                                                </form>
                                            )}
                                        </div>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
}
